import { useEffect, useRef, useState } from "react";
import { Hint } from "./Hint";
import { hiddenCss, hiddenKey, numCss, parseHidden, widthCss } from "./hide";
import { store, stored } from "./Menu";
import {
  ariaSort,
  nextSort,
  parseSortParam,
  readSort,
  type Sort,
  type SortColumn,
  sortOptions,
  sortParam,
  writeSort,
} from "./sort";

/** Keep the sort of one table in the URL parameter `param`, so a reload and a shared link keep it. */
export function useSort<T>(param: string, columns: readonly SortColumn<T>[]) {
  const [sort, setSortState] = useState<Sort | null>(() => readSort(location.search, param, columns));
  const setSort = (next: Sort | null) => {
    setSortState(next);
    history.replaceState(history.state, "", `${location.pathname}${writeSort(location.search, param, next)}${location.hash}`);
  };
  return [sort, setSort] as const;
}

function Arrow({ dir }: { dir: "ascending" | "descending" | "none" }) {
  return (
    <span className="sort-arrow" aria-hidden="true">
      {dir === "ascending" ? "▲" : dir === "descending" ? "▼" : "↕"}
    </span>
  );
}

/** The class of a header cell: `num` for a number column, then the classes of the caller. */
export const thClass = (column: Pick<SortColumn<unknown>, "num">, className?: string) => [column.num && "num", className].filter(Boolean).join(" ") || undefined;

export function SortTh<T>({
  column,
  sort,
  onSort,
  className,
}: {
  column: SortColumn<T>;
  sort: Sort | null;
  onSort: (next: Sort) => void;
  className?: string;
}) {
  const dir = ariaSort(sort, column.key);
  return (
    <th scope="col" aria-sort={dir} className={thClass(column, className)}>
      <Hint text={column.help} stop={false} className="help-text">
        <button type="button" className="sort" data-testid={`sort-${column.key}`} onClick={() => onSort(nextSort(sort, column))}>
          {column.label}
          <Arrow dir={dir} />
        </button>
      </Hint>
    </th>
  );
}

/** The tools above a list: the phone "Sort by" list (cards have no header row) and the Columns menu. */
export function SortSelect<T>({
  id,
  columns,
  sort,
  onSort,
  fallback,
}: {
  id: string;
  columns: readonly SortColumn<T>[];
  sort: Sort | null;
  onSort: (next: Sort | null) => void;
  fallback: string;
}) {
  return (
    <div className="list-tools">
      <div className="sort-select">
        <label htmlFor={id}>Sort by</label>
        <select
          id={id}
          data-testid={id}
          value={sortParam(sort)}
          onChange={(e) => onSort(e.target.value ? parseSortParam(e.target.value, columns) : null)}
        >
          <option value="">{fallback}</option>
          {sortOptions(columns).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <ColumnMenu id={id} columns={columns} />
    </div>
  );
}

/** Lets the user hide and show the columns of the table with `data-cols={id}`; this browser keeps the choice. */
function ColumnMenu<T>({ id, columns }: { id: string; columns: readonly SortColumn<T>[] }) {
  const keys = columns.map((column) => column.key);
  const off = columns.filter((column) => column.off).map((column) => column.key);
  const [hidden, setHidden] = useState(() => parseHidden(stored(hiddenKey(id)), keys, off));
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (focus: boolean) => {
      if (menu.current) menu.current.open = false;
      if (focus) menu.current?.querySelector("summary")?.focus();
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && close(true);
    const outside = (e: PointerEvent) => !menu.current?.contains(e.target as Node) && close(false);
    document.addEventListener("keydown", key);
    document.addEventListener("pointerdown", outside);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("pointerdown", outside);
    };
  }, [open]);
  const save = (next: string[]) => {
    setHidden(next);
    store(hiddenKey(id), JSON.stringify(next));
  };
  return (
    <>
      <style>{`${hiddenCss(id, keys, hidden)}\n${numCss(id, columns.map((column) => Boolean(column.num)))}\n${widthCss(id, keys, columns.map((column) => column.width), hidden)}`}</style>
      <details
        ref={menu}
        className="column-menu"
        data-testid={`${id}-columns`}
        onToggle={(e) => setOpen(e.currentTarget.open)}
      >
        <summary>Columns{hidden.length > 0 && ` (${hidden.length} hidden)`}</summary>
        <fieldset>
          <legend className="visually-hidden">Columns to show</legend>
          {columns.map((column, index) => (
            <label key={column.key}>
              <input
                type="checkbox"
                checked={!hidden.includes(column.key)}
                disabled={index === 0}
                onChange={(e) => save(e.target.checked ? hidden.filter((key) => key !== column.key) : [...hidden, column.key])}
              />{" "}
              {column.label}
            </label>
          ))}
          {hidden.length > 0 && (
            <button type="button" onClick={() => save([])}>
              Show all
            </button>
          )}
        </fieldset>
      </details>
    </>
  );
}
